import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import useSWR from "swr";
import { Combobox, Transition } from "@headlessui/react";
import type { IGQueryTypes } from "@shared-libs/instagram";
import { FiInstagram } from "react-icons/fi";
import { MdVerified } from "react-icons/md";
import { BiChevronRight } from "react-icons/bi";
import { MagnifyingGlassIcon, ArrowPathIcon, ExclamationCircleIcon } from "@heroicons/react/20/solid";
import { RiHashtag } from "react-icons/ri";
import { motion, AnimatePresence } from "framer-motion";
import { Spinner } from "@/components/ui/spinner";
import { normalizeSocialHandle } from "@/utils/social-handle";
import { adaptiveDebounceMs } from "@/utils/search-debounce";

/**
 * Proxy Instagram CDN URL through Cloudflare Workers edge proxy
 * Uses custom domain route: *.outfame.com/ig-assets/*
 */
function getProxiedImageUrl(url: string): string {
  if (!url) return "/img/default_avatar.jpeg";
  return `https://www.outfame.com/ig-assets/?url=${encodeURIComponent(url)}`;
}

/**
 * Get proxied avatar URL for Instagram users
 * Instagram CDN URLs are IP-bound, so we proxy through our server
 * FALLBACK ONLY - prefer using getProxiedImageUrl with URL from search results
 */
function getAvatarUrl(username: string | undefined): string {
  if (!username) return "/img/default_avatar.jpeg";
  return `/api/instagram/avatar-proxy?username=${encodeURIComponent(username)}`;
}

/**
 * Avatar with skeleton loader and fade-in animation
 * Accepts optional avatarUrl for direct edge proxy (preferred) or falls back to username-based proxy
 */
function AvatarWithLoader(t0) {
  const $ = _c(34);
  const {
    username,
    avatarUrl,
    size: t1,
    className: t2,
    ringClassName: t3
  } = t0;
  const size = t1 === undefined ? 40 : t1;
  const className = t2 === undefined ? "" : t2;
  const ringClassName = t3 === undefined ? "" : t3;
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsLoaded(false);
      setHasError(false);
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  let t5;
  if ($[1] !== avatarUrl || $[2] !== username) {
    t5 = [username, avatarUrl];
    $[1] = avatarUrl;
    $[2] = username;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  useEffect(t4, t5);
  let t6;
  if ($[4] !== avatarUrl || $[5] !== hasError || $[6] !== username) {
    t6 = hasError ? "/img/default_avatar.jpeg" : avatarUrl ? getProxiedImageUrl(avatarUrl) : getAvatarUrl(username);
    $[4] = avatarUrl;
    $[5] = hasError;
    $[6] = username;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const imageSrc = t6;
  const t7 = `relative ${className}`;
  let t8;
  if ($[8] !== size) {
    t8 = {
      width: size,
      height: size
    };
    $[8] = size;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== hasError || $[11] !== isLoaded) {
    t9 = !isLoaded && !hasError && <motion.div initial={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="absolute inset-0 rounded-full" style={{
      background: "linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%)",
      backgroundSize: "300% 100%",
      animation: "skeleton-shimmer 2s ease-in-out infinite"
    }} />;
    $[10] = hasError;
    $[11] = isLoaded;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t9) {
    t10 = <AnimatePresence>{t9}</AnimatePresence>;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      opacity: 0
    };
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = isLoaded ? 1 : 0;
  let t13;
  if ($[16] !== t12) {
    t13 = {
      opacity: t12
    };
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      duration: 0.25,
      ease: "easeOut"
    };
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const t15 = `h-full w-full rounded-full object-cover ${ringClassName}`;
  let t16;
  let t17;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setIsLoaded(true);
    t17 = () => {
      setHasError(true);
      setIsLoaded(true);
    };
    $[19] = t16;
    $[20] = t17;
  } else {
    t16 = $[19];
    t17 = $[20];
  }
  let t18;
  if ($[21] !== imageSrc || $[22] !== size || $[23] !== t15 || $[24] !== username) {
    t18 = <Image unoptimized={true} src={imageSrc} alt={username} width={size} height={size} className={t15} onLoad={t16} onError={t17} />;
    $[21] = imageSrc;
    $[22] = size;
    $[23] = t15;
    $[24] = username;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== t13 || $[27] !== t18) {
    t19 = <motion.div initial={t11} animate={t13} transition={t14} className="absolute inset-0">{t18}</motion.div>;
    $[26] = t13;
    $[27] = t18;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== t10 || $[30] !== t19 || $[31] !== t7 || $[32] !== t8) {
    t20 = <div className={t7} style={t8}>{t10}{t19}</div>;
    $[29] = t10;
    $[30] = t19;
    $[31] = t7;
    $[32] = t8;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  return t20;
}

// Premium shimmer skeleton with smooth, spread-out animation
const SkeletonPulse = t0 => {
  const $ = _c(5);
  const {
    className,
    style
  } = t0;
  const t1 = `relative overflow-hidden ${className}`;
  let t2;
  if ($[0] !== style) {
    t2 = {
      background: "linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%)",
      backgroundSize: "300% 100%",
      animation: "skeleton-shimmer 2s ease-in-out infinite",
      ...style
    };
    $[0] = style;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <div className={t1} style={t2} />;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};

// Skeleton loading component for search results - NO motion wrapper, just static structure
const SearchResultSkeleton = t0 => {
  const $ = _c(12);
  const {
    index,
    searchType
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SkeletonPulse className="h-10 w-10 shrink-0 rounded-full" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = `${70 + index * 8}%`;
  let t3;
  if ($[1] !== t2) {
    t3 = <SkeletonPulse className="h-4 rounded-md" style={{
      width: t2,
      maxWidth: "120px"
    }} />;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== index || $[4] !== searchType) {
    t4 = searchType === "users" && <SkeletonPulse className="h-3 rounded-md" style={{
      width: `${85 + index * 5}%`,
      maxWidth: "160px"
    }} />;
    $[3] = index;
    $[4] = searchType;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <div className="flex-1 space-y-2.5">{t3}{t4}</div>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SkeletonPulse className="h-5 w-5 shrink-0 rounded-sm" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5) {
    t7 = <div className="flex items-center space-x-3 px-4 py-3">{t1}{t5}{t6}</div>;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
};

/**
 * Unified search result row that crossfades between skeleton and real content
 * The outer structure stays fixed - only inner content transitions
 */
function SearchResultRowWithTransition(t0) {
  const $ = _c(24);
  const {
    result,
    index,
    isLoading,
    searchType,
    totalResults,
    isActive: t1
  } = t0;
  const isActive = t1 === undefined ? false : t1;
  const showSkeleton = isLoading || !result;
  const isLastItem = index === totalResults - 1;
  const t2 = `relative ${!isLastItem ? "border-b border-gray-50/50" : ""}`;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 1
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const t4 = showSkeleton ? 1 : 0;
  let t5;
  if ($[1] !== t4) {
    t5 = {
      opacity: t4
    };
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      duration: 0.2
    };
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const t7 = showSkeleton ? "relative" : "pointer-events-none absolute inset-0";
  let t8;
  if ($[4] !== index || $[5] !== searchType) {
    t8 = <SearchResultSkeleton index={index} searchType={searchType} />;
    $[4] = index;
    $[5] = searchType;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== t5 || $[8] !== t7 || $[9] !== t8) {
    t9 = <motion.div initial={t3} animate={t5} transition={t6} className={t7}>{t8}</motion.div>;
    $[7] = t5;
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== isActive || $[12] !== result || $[13] !== showSkeleton) {
    t10 = result && "username" in result && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: showSkeleton ? 0 : 1
    }} transition={{
      duration: 0.25,
      delay: 0.1
    }} className={showSkeleton ? "pointer-events-none absolute inset-0" : "relative"}><div className={`flex cursor-pointer items-center space-x-3 px-4 py-3 transition-colors duration-150 ease-out ${isActive ? "bg-gray-50" : ""}`}><div className="relative shrink-0"><AvatarWithLoader username={result.username} avatarUrl={result.profile_pic_url} size={40} ringClassName="ring-2 ring-gray-100" />{result.is_verified && <div className="absolute -bottom-0.5 -right-0.5 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-white ring-1 ring-white"><MdVerified className="h-3.5 w-3.5 text-[#00a0ff]" /></div>}</div><div className="flex w-full min-w-0 items-center justify-between"><div className="min-w-0"><p className="truncate font-medium text-gray-900">@{result.username?.trim()}</p>{result.full_name?.trim() && <p className="truncate text-sm text-gray-500">{result.full_name.trim()}</p>}</div><BiChevronRight className="h-5 w-5 shrink-0 text-gray-400" /></div></div></motion.div>;
    $[11] = isActive;
    $[12] = result;
    $[13] = showSkeleton;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== isActive || $[16] !== result || $[17] !== showSkeleton) {
    t11 = result && "name" in result && !("username" in result) && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: showSkeleton ? 0 : 1
    }} transition={{
      duration: 0.25,
      delay: 0.1
    }} className={showSkeleton ? "pointer-events-none absolute inset-0" : "relative"}><div className={`flex cursor-pointer items-center space-x-3 px-4 py-3 transition-colors duration-150 ease-out ${isActive ? "bg-gray-50" : ""}`}><div className="bg-linear-to-br flex h-10 w-10 shrink-0 items-center justify-center rounded-full from-purple-100 to-pink-100"><RiHashtag className="h-5 w-5 text-purple-500" /></div><div className="flex w-full min-w-0 items-center justify-between"><div className="min-w-0"><p className="truncate font-medium text-gray-900">#{result.name}</p>{result.mediaCount > 0 && <p className="text-xs text-gray-400">{result.mediaCount.toLocaleString()} posts</p>}</div><BiChevronRight className="h-5 w-5 shrink-0 text-gray-400" /></div></div></motion.div>;
    $[15] = isActive;
    $[16] = result;
    $[17] = showSkeleton;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10 || $[20] !== t11 || $[21] !== t2 || $[22] !== t9) {
    t12 = <div className={t2}>{t9}{t10}{t11}</div>;
    $[19] = t10;
    $[20] = t11;
    $[21] = t2;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
}

// Empty state component
const EmptyState = t0 => {
  const $ = _c(12);
  const {
    query,
    searchType
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0,
      scale: 0.95
    };
    t2 = {
      opacity: 1,
      scale: 1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== searchType) {
    t3 = <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">{searchType === "users" ? <FiInstagram className="h-6 w-6 text-gray-400" /> : <RiHashtag className="h-6 w-6 text-gray-400" />}</div>;
    $[2] = searchType;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== query) {
    t4 = <p className="text-sm font-medium text-gray-700">No results for "{query}"</p>;
    $[4] = query;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = searchType === "users" ? "Try searching for a username like \"selenagomez\"" : "Try searching for a hashtag like \"fitness\"";
  let t6;
  if ($[6] !== t5) {
    t6 = <p className="mt-1 text-xs text-gray-500">{t5}</p>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t3 || $[9] !== t4 || $[10] !== t6) {
    t7 = <motion.div initial={t1} animate={t2} className="px-5 py-8 text-center">{t3}{t4}{t6}</motion.div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
};

// Error state component
const ErrorState = t0 => {
  const $ = _c(7);
  const {
    onRetry
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0,
      scale: 0.95
    };
    t2 = {
      opacity: 1,
      scale: 1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-50"><ExclamationCircleIcon className="h-6 w-6 text-red-400" /></div>;
    t4 = <p className="text-sm font-medium text-gray-700">Unable to search right now</p>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ArrowPathIcon className="h-3.5 w-3.5" />;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== onRetry) {
    t6 = <motion.div initial={t1} animate={t2} className="px-5 py-6 text-center">{t3}{t4}<button onClick={onRetry} className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-purple-50 px-3 py-1.5 text-xs font-medium text-purple-600 transition-colors hover:bg-purple-100">{t5}Try again</button></motion.div>;
    $[5] = onRetry;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
};
export interface InstagramAccount {
  user_id: string;
  username: string;
  full_name?: string;
  avatar_url?: string;
  profile_pic_url?: string;
  is_verified?: boolean;
  is_private?: boolean;
}
export interface InstagramHashtag {
  target_id: string;
  name: string;
  mediaCount: number;
  search_result_subtitle: string;
}
type InstagramSearchResult = InstagramAccount | InstagramHashtag;

// Dropdown container styles (shared between skeleton and results)
const dropdownClassName = `
  absolute left-0 top-full mt-2 max-h-[300px] overflow-auto rounded-2xl z-50 w-full bg-white
  border border-gray-100 shadow-2xl shadow-black/10
  scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent
  ring-1 ring-black/5
`;

/**
 * Instagram Search Field - Simplified Implementation
 *
 * State architecture:
 * - inputValue: What user sees in input (updates immediately on keypress)
 * - debouncedValue: Adaptively debounced inputValue (triggers API once the typist pauses; 300ms-1.2s)
 * - selectedResult: The currently selected result from dropdown
 * - isFocused: Whether input has focus
 * - results: Array of search results from API
 *
 * Key insight: "isConfirmed" is DERIVED, not tracked:
 * - A selection is "confirmed" when inputValue matches the selected result's value
 * - This naturally prevents searches when user hasn't selected or has modified their selection
 * - When they start typing, inputValue diverges from selection, enabling search
 */
export default function InstagramSearchField(t0) {
  const $ = _c(119);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let props;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== t1) {
    ({
      onResultSelected: t2,
      onValueChange: t3,
      onEnterKeyPressed: t4,
      disabled: t5,
      autocompleteOnSelect: t6,
      searchType: t7,
      autoFocus: t8,
      disableSearch: t9,
      defaultValue: t10,
      ...props
    } = t1);
    $[2] = t1;
    $[3] = props;
    $[4] = t10;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    props = $[3];
    t10 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
    t8 = $[11];
    t9 = $[12];
  }
  let t11;
  if ($[13] !== t2) {
    t11 = t2 === undefined ? _temp : t2;
    $[13] = t2;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const onResultSelected = t11;
  let t12;
  if ($[15] !== t3) {
    t12 = t3 === undefined ? _temp2 : t3;
    $[15] = t3;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const onValueChange = t12;
  let t13;
  if ($[17] !== t4) {
    t13 = t4 === undefined ? _temp3 : t4;
    $[17] = t4;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const onEnterKeyPressed = t13;
  const disabled = t5 === undefined ? false : t5;
  const autocompleteOnSelect = t6 === undefined ? true : t6;
  const searchType = t7 === undefined ? "users" : t7;
  const autoFocus = t8 === undefined ? true : t8;
  const disableSearch = t9 === undefined ? false : t9;
  const defaultValue = t10 === undefined ? "" : t10;
  const [inputValue, setInputValue] = useState(defaultValue);
  const [selectedResult, setSelectedResult] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  let t14;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [];
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  const [results, setResults] = useState(t14);
  const lastInteractionRef = useRef(0);
  const [debouncedValue, setDebouncedValue] = useState(defaultValue);
  let t15;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = [];
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const keyTimesRef = useRef(t15);
  let t16;
  let t17;
  if ($[21] !== inputValue) {
    t16 = () => {
      const times = keyTimesRef.current;
      times.push(Date.now());
      if (times.length > 6) {
        times.shift();
      }
      const timer = setTimeout(() => setDebouncedValue(inputValue), adaptiveDebounceMs(times));
      return () => clearTimeout(timer);
    };
    t17 = [inputValue];
    $[21] = inputValue;
    $[22] = t16;
    $[23] = t17;
  } else {
    t16 = $[22];
    t17 = $[23];
  }
  useEffect(t16, t17);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  let t18;
  let t19;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      const markInteraction = () => {
        lastInteractionRef.current = Date.now();
      };
      document.addEventListener("mousedown", markInteraction);
      document.addEventListener("touchstart", markInteraction, {
        passive: true
      });
      return () => {
        document.removeEventListener("mousedown", markInteraction);
        document.removeEventListener("touchstart", markInteraction);
      };
    };
    t19 = [];
    $[24] = t18;
    $[25] = t19;
  } else {
    t18 = $[24];
    t19 = $[25];
  }
  useEffect(t18, t19);
  const getResultValue = _temp4;
  let t20;
  if ($[26] !== inputValue || $[27] !== selectedResult) {
    t20 = selectedResult && inputValue === getResultValue(selectedResult);
    $[26] = inputValue;
    $[27] = selectedResult;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  const isConfirmed = t20;
  const searchKey = !disableSearch && debouncedValue.length >= 2 && !isConfirmed ? debouncedValue : null;
  const isPendingSearch = inputValue.length >= 2 && !isConfirmed && inputValue !== debouncedValue;
  let t21;
  if ($[29] !== searchType) {
    t21 = async query => {
      console.log("\uD83D\uDD0D API fetch for:", query);
      const targets = await fetch(`/api/instagram/search-targets?type=${searchType as IGQueryTypes}&search=${query}`).then(_temp5);
      console.log(`✅ Got ${Array.isArray(targets) ? targets.length : 0} results for: ${query}`);
      const resultArray = Array.isArray(targets) ? targets : [];
      setResults(resultArray);
      return resultArray;
    };
    $[29] = searchType;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  const fetchFromRapidAPI = t21;
  let t22;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      onError: _temp6,
      errorRetryCount: 2,
      revalidateOnFocus: false,
      dedupingInterval: 500
    };
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  const {
    error: error_0,
    isValidating,
    mutate: revalidate
  } = useSWR(searchKey, fetchFromRapidAPI, t22);
  const isLoading = isPendingSearch || isValidating;
  const hasError = !!error_0 && debouncedValue.length >= 2 && !isConfirmed;
  const hasResults = !isLoading && !hasError && results.length > 0;
  const showEmpty = !isLoading && !hasError && results.length === 0 && debouncedValue.length >= 2 && !isConfirmed && searchKey !== null;
  const showDropdown = isFocused && inputValue.length >= 2 && !isConfirmed;
  let t23;
  let t24;
  if ($[32] !== autoFocus) {
    t23 = () => {
      if (inputRef.current && !inputRef.current.value && autoFocus) {
        inputRef.current.focus();
      }
    };
    t24 = [autoFocus];
    $[32] = autoFocus;
    $[33] = t23;
    $[34] = t24;
  } else {
    t23 = $[33];
    t24 = $[34];
  }
  useEffect(t23, t24);
  let t25;
  if ($[35] !== defaultValue || $[36] !== selectedResult) {
    t25 = () => {
      if (defaultValue && inputRef.current) {
        inputRef.current.value = defaultValue;
        setInputValue(defaultValue);
        if (!selectedResult) {
          setSelectedResult({
            username: defaultValue,
            user_id: ""
          } as InstagramAccount);
        }
      }
    };
    $[35] = defaultValue;
    $[36] = selectedResult;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  let t26;
  if ($[38] !== defaultValue) {
    t26 = [defaultValue];
    $[38] = defaultValue;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  useEffect(t25, t26);
  const compareResults = _temp7;
  let t27;
  if ($[40] !== onValueChange) {
    t27 = value_0 => {
      setInputValue(value_0);
      onValueChange(value_0);
    };
    $[40] = onValueChange;
    $[41] = t27;
  } else {
    t27 = $[41];
  }
  const handleInputChange = t27;
  let t28;
  if ($[42] !== revalidate || $[43] !== searchKey) {
    t28 = () => {
      if (searchKey) {
        revalidate();
      }
    };
    $[42] = revalidate;
    $[43] = searchKey;
    $[44] = t28;
  } else {
    t28 = $[44];
  }
  const handleRetry = t28;
  let t29;
  if ($[45] !== autocompleteOnSelect || $[46] !== onResultSelected || $[47] !== onValueChange) {
    t29 = result_1 => {
      if (!result_1) {
        return;
      }
      console.log("Selected result:", result_1);
      setSelectedResult(result_1);
      onResultSelected(result_1);
      const value_1 = getResultValue(result_1);
      onValueChange(value_1);
      setIsFocused(false);
      if (autocompleteOnSelect) {
        setInputValue(value_1);
        if (inputRef.current) {
          inputRef.current.value = value_1;
        }
      } else {
        setInputValue("");
        setSelectedResult(null);
        if (inputRef.current) {
          inputRef.current.value = "";
        }
      }
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.blur();
        }
      }, 10);
    };
    $[45] = autocompleteOnSelect;
    $[46] = onResultSelected;
    $[47] = onValueChange;
    $[48] = t29;
  } else {
    t29 = $[48];
  }
  const handleResultSelect = t29;
  const t30 = disabled || (props as {
    disabled?: boolean;
  }).disabled;
  let t31;
  if ($[49] !== handleResultSelect) {
    t31 = result_2 => {
      if (result_2) {
        handleResultSelect(result_2);
      }
    };
    $[49] = handleResultSelect;
    $[50] = t31;
  } else {
    t31 = $[50];
  }
  let t32;
  if ($[51] !== isConfirmed || $[52] !== isFocused || $[53] !== searchType || $[54] !== selectedResult) {
    t32 = searchType === "users" ? <div className="absolute left-3.5 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center"><AnimatePresence mode="wait" initial={false}>{selectedResult && "username" in selectedResult && isConfirmed ? <motion.div key="avatar" initial={{
          scale: 0.6,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.6,
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: [0.4, 0, 0.2, 1]
        }}><AvatarWithLoader username={selectedResult.username} avatarUrl={selectedResult.profile_pic_url} size={28} ringClassName="ring-2 ring-white shadow-sm" /></motion.div> : <motion.div key="icon" initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.8,
          opacity: 0
        }} transition={{
          duration: 0.15
        }}><FiInstagram className={`h-5 w-5 transition-colors duration-200 ${isFocused ? "text-gray-900" : "text-gray-400"}`} /></motion.div>}</AnimatePresence></div> : <MagnifyingGlassIcon className={`absolute left-5 top-1/2 z-10 h-5 w-5 -translate-y-1/2 transform transition-colors duration-200 ${isFocused ? "text-purple-500" : "text-gray-500"}`} />;
    $[51] = isConfirmed;
    $[52] = isFocused;
    $[53] = searchType;
    $[54] = selectedResult;
    $[55] = t32;
  } else {
    t32 = $[55];
  }
  const t33 = `${searchType === "users" ? "selenagomez" : "fitness"}`;
  let t34;
  let t35;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = () => setIsFocused(true);
    t35 = event => {
      event.preventDefault();
      const timeSinceInteraction = Date.now() - lastInteractionRef.current;
      if (timeSinceInteraction > 200 && !event.relatedTarget) {
        return;
      }
      setTimeout(() => {
        if (dropdownRef.current?.contains(document.activeElement)) {
          return;
        }
        setIsFocused(false);
      }, 150);
    };
    $[56] = t34;
    $[57] = t35;
  } else {
    t34 = $[56];
    t35 = $[57];
  }
  let t36;
  if ($[58] !== handleInputChange) {
    t36 = e_0 => handleInputChange(e_0.target.value);
    $[58] = handleInputChange;
    $[59] = t36;
  } else {
    t36 = $[59];
  }
  let t37;
  if ($[60] !== onEnterKeyPressed) {
    t37 = e_1 => {
      if (e_1.key === "Tab") {
        e_1.preventDefault();
        const focusable = Array.from(document.querySelectorAll("button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])"));
        const currentIndex = focusable.indexOf(document.activeElement as Element);
        const nextIndex = (currentIndex + 1) % focusable.length;
        const nextElement = focusable[nextIndex] as HTMLElement;
        nextElement.focus();
      }
      if (e_1.key === "Enter") {
        e_1.preventDefault();
        setIsFocused(false);
        if (onEnterKeyPressed) {
          onEnterKeyPressed();
        }
      }
      const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "Escape"];
      if (allowedKeys.includes(e_1.key)) {
        return;
      }
      const regex = /^[a-zA-Z0-9_.]*$/;
      if (!regex.test(e_1.key)) {
        e_1.preventDefault();
      }
    };
    $[60] = onEnterKeyPressed;
    $[61] = t37;
  } else {
    t37 = $[61];
  }
  let t38;
  if ($[62] !== handleInputChange) {
    t38 = e_2 => {
      e_2.preventDefault();
      const text = e_2.clipboardData.getData("text");
      const parsed = normalizeSocialHandle(text, "instagram");
      const nextValue = parsed.ok ? parsed.handle : text.replace(/[\r\n\t]+/g, " ").trim().slice(0, 200);
      if (e_2.target instanceof HTMLInputElement) {
        e_2.target.value = nextValue;
        handleInputChange(nextValue);
      }
    };
    $[62] = handleInputChange;
    $[63] = t38;
  } else {
    t38 = $[63];
  }
  let t39;
  if ($[64] !== inputValue) {
    t39 = () => inputValue;
    $[64] = inputValue;
    $[65] = t39;
  } else {
    t39 = $[65];
  }
  const t40 = `block h-14 w-full rounded-[16px] border-0 bg-[#f5f5f5] px-4 py-3 pl-[52px] text-base font-semibold tracking-[0.009em] text-gray-900 outline-none transition-all duration-200 ease-out placeholder:font-normal placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-inset focus:ring-gray-900 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-50 ${disabled ? "bg-gray-100" : ""} `;
  let t41;
  if ($[66] !== autoFocus || $[67] !== disabled || $[68] !== props || $[69] !== t33 || $[70] !== t36 || $[71] !== t37 || $[72] !== t38 || $[73] !== t39 || $[74] !== t40) {
    t41 = <Combobox.Button as="div" className="flex"><Combobox.Input ref={inputRef} placeholder={t33} onFocus={t34} onBlur={t35} onClick={_temp8} onChange={t36} onKeyDown={t37} onPaste={t38} displayValue={t39} className={t40} required={autoFocus} readOnly={disabled} autoComplete="off" autoCorrect="off" autoCapitalize="off" data-1p-ignore={true} data-lpignore="true" data-bwignore={true} data-form-type="other" data-protonpass-ignore={true} spellCheck={false} maxLength={50} minLength={2} autoFocus={autoFocus} {...props} /></Combobox.Button>;
    $[66] = autoFocus;
    $[67] = disabled;
    $[68] = props;
    $[69] = t33;
    $[70] = t36;
    $[71] = t37;
    $[72] = t38;
    $[73] = t39;
    $[74] = t40;
    $[75] = t41;
  } else {
    t41 = $[75];
  }
  let t42;
  if ($[76] !== isLoading) {
    t42 = isLoading && <motion.div initial={{
      opacity: 0,
      x: 4
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: 4
    }} transition={{
      duration: 0.2,
      ease: [0.22, 1, 0.36, 1]
    }} className="absolute right-4 top-1/2 z-10 flex -translate-y-1/2 items-center gap-2"><span className="text-xs font-medium text-gray-400">Searching</span><Spinner size="sm" className="text-gray-950" /></motion.div>;
    $[76] = isLoading;
    $[77] = t42;
  } else {
    t42 = $[77];
  }
  let t43;
  if ($[78] !== t42) {
    t43 = <AnimatePresence>{t42}</AnimatePresence>;
    $[78] = t42;
    $[79] = t43;
  } else {
    t43 = $[79];
  }
  let t44;
  if ($[80] !== t32 || $[81] !== t41 || $[82] !== t43) {
    t44 = <div className="group relative"><div className="relative"><div className="relative">{t32}{t41}{t43}</div></div></div>;
    $[80] = t32;
    $[81] = t41;
    $[82] = t43;
    $[83] = t44;
  } else {
    t44 = $[83];
  }
  const t45 = showDropdown && (isLoading || hasResults);
  const t46 = isLoading ? 0.92 : 1;
  let t47;
  if ($[84] !== t46) {
    t47 = {
      opacity: t46,
      transition: "opacity 300ms cubic-bezier(0.22, 1, 0.36, 1)"
    };
    $[84] = t46;
    $[85] = t47;
  } else {
    t47 = $[85];
  }
  let t48;
  if ($[86] !== results.length) {
    t48 = Array.from({
      length: Math.max(4, results.length)
    });
    $[86] = results.length;
    $[87] = t48;
  } else {
    t48 = $[87];
  }
  let t49;
  if ($[88] !== isLoading || $[89] !== results || $[90] !== searchType || $[91] !== t48) {
    t49 = t48.map((_, index) => {
      const result_3 = results[index] || null;
      const key = result_3 ? "username" in result_3 ? result_3.username : result_3.name : `skeleton-${index}`;
      return <Combobox.Option key={key} value={result_3} disabled={!result_3} className="outline-none">{t50 => {
          const {
            active
          } = t50;
          return <SearchResultRowWithTransition result={result_3} index={index} isLoading={isLoading} searchType={searchType} totalResults={Math.max(4, results.length)} isActive={active} />;
        }}</Combobox.Option>;
    });
    $[88] = isLoading;
    $[89] = results;
    $[90] = searchType;
    $[91] = t48;
    $[92] = t49;
  } else {
    t49 = $[92];
  }
  let t50;
  if ($[93] !== t47 || $[94] !== t49) {
    t50 = <Combobox.Options static={true} className={dropdownClassName} ref={dropdownRef} style={t47}>{t49}</Combobox.Options>;
    $[93] = t47;
    $[94] = t49;
    $[95] = t50;
  } else {
    t50 = $[95];
  }
  let t51;
  if ($[96] !== t45 || $[97] !== t50) {
    t51 = <Transition as="div" className="relative" show={t45} enter="transition-all duration-200 ease-out" enterFrom="opacity-0 translate-y-1" enterTo="opacity-100 translate-y-0" leave="transition-all duration-150 ease-in" leaveFrom="opacity-100 translate-y-0" leaveTo="opacity-0 translate-y-1">{t50}</Transition>;
    $[96] = t45;
    $[97] = t50;
    $[98] = t51;
  } else {
    t51 = $[98];
  }
  let t52;
  if ($[99] !== selectedResult || $[100] !== t30 || $[101] !== t31 || $[102] !== t44 || $[103] !== t51) {
    t52 = <Combobox value={selectedResult} by={compareResults} disabled={t30} onChange={t31}>{t44}{t51}</Combobox>;
    $[99] = selectedResult;
    $[100] = t30;
    $[101] = t31;
    $[102] = t44;
    $[103] = t51;
    $[104] = t52;
  } else {
    t52 = $[104];
  }
  let t53;
  if ($[105] !== handleRetry || $[106] !== hasError || $[107] !== hasResults || $[108] !== inputValue || $[109] !== isLoading || $[110] !== searchType || $[111] !== showDropdown || $[112] !== showEmpty) {
    t53 = showDropdown && (showEmpty || hasError) && !isLoading && !hasResults && <motion.div initial={{
      opacity: 0,
      y: 4
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 4
    }} transition={{
      duration: 0.15
    }} className={dropdownClassName}>{hasError && <ErrorState onRetry={handleRetry} />}{showEmpty && !hasError && <EmptyState query={inputValue} searchType={searchType} />}</motion.div>;
    $[105] = handleRetry;
    $[106] = hasError;
    $[107] = hasResults;
    $[108] = inputValue;
    $[109] = isLoading;
    $[110] = searchType;
    $[111] = showDropdown;
    $[112] = showEmpty;
    $[113] = t53;
  } else {
    t53 = $[113];
  }
  let t54;
  if ($[114] !== t53) {
    t54 = <div className="relative"><AnimatePresence>{t53}</AnimatePresence></div>;
    $[114] = t53;
    $[115] = t54;
  } else {
    t54 = $[115];
  }
  let t55;
  if ($[116] !== t52 || $[117] !== t54) {
    t55 = <div className="relative">{t52}{t54}</div>;
    $[116] = t52;
    $[117] = t54;
    $[118] = t55;
  } else {
    t55 = $[118];
  }
  return t55;
}
function _temp8(e) {
  if ((e.relatedTarget as Element)?.id?.includes("headlessui-combobox-button")) {
    return;
  }
  !open && (e.currentTarget.nextSibling as HTMLElement)?.click();
}
function _temp7(a, b) {
  if (a && b) {
    if ("username" in a && "username" in b) {
      return a.username.toLowerCase() === b.username.toLowerCase();
    } else {
      if ("name" in a && "name" in b) {
        return a.name.toLowerCase() === b.name.toLowerCase();
      }
    }
  }
  return false;
}
function _temp6(error) {
  return console.error("SWR error:", error);
}
function _temp5(res) {
  return res.json();
}
function _temp4(result_0) {
  if (!result_0) {
    return "";
  }
  return "username" in result_0 ? result_0.username : result_0.name;
}
function _temp3() {}
function _temp2(value) {}
function _temp(result) {}